/* State for dealing with currently active recipes
 *
 * TODO plan
 *
 */

import {atom} from 'jotai';
import {atomWithImmer} from 'jotai-immer';
import Fraction from 'fraction.js';

import {store} from './global';
import {genSpeech} from "./audio";
import {COOKING_SERVER, categoricalColor, is_dict} from "../utils";

//import {uniqueId} from "../nk-utils";

export type StatusValue = "todo" | "cur" | "done";
export type ObjType = "ingr" | "step";

export interface IngredientT {
  id: string;
  min: number;
  max: number;
  unit: string;
  full_item: string;
  main_item: string;
  source: string;
  type: string;
  importance: number;
  notes: string[];
  alternates?: IngredientT[];
}

export interface TimerT {
  name: string;
  h: number;
  m: number;
  s: number;
}

export interface StepT {
  text: string;
  ingredients?: string[]; // list of ingredient ids
  timers?: TimerT[];
}

export interface Status {
  todo: Record<string, any>;
  cur: Record<string, any>;
  done: Record<string, any>;
}

// container for an ingredient or step that includes the section name as well as status and id
export interface ContainerT<T> {
  obj: T;
  section: string;
  id: string;
  status: StatusValue;
}

export interface RecipeT {
  name: string;
  url: string;
  color: string;
  ingredients: ContainerT<IngredientT>[];
  steps: ContainerT<StepT>[];
  scale: number;
  description?: string;
  image?: string[];
  servings?: number;
  annotations?: any[];
  times: Record<string, number>;
  [key: string]: any;
}

// session id, initialized when we start
export const sessionIdAtom = atom(`session-${(Date.now()/1000).toFixed(0)}`);

// map from recipe name to recipe
export const activesAtom = atomWithImmer<Record<string, RecipeT>>({});

// current error if any
export const errorAtom = atom("");

// name of the currently focused recipe
export const focusedAtom = atom("");

// gets the currently focused recipe
export const currentAtom = atom((get) => {
  const name = get(focusedAtom);
  const actives = get(activesAtom);
  return actives[name];
});

// atom to update some data in the current recipe
export const updateCurrentAtom = atom(null, (get, set, {field, value}) => {
  const name = get(focusedAtom);
  set(activesAtom, (actives) => {
    actives[name][field] = value;
  });
});

// An item which returns a function to get the current state of a step or ingredient container, by
// id
export const objByIdAtom = atom((get) => {
  return (id: string) => {
    const r = get(currentAtom);
    const lst = r.ingredients.concat(r.steps);
    const idx = lst.findIndex((el: ContainerT<any>) => el.id === id);
    return lst[idx];
  };
});


// where to jump to
export const jumpAtom = atom(null);


// loads a recipe from the cooking server with given id
export const loadRecipe = (id) => {
  if (!id) {
    return;
  }
  console.log("Fetching active recipe from server", id);
  fetch(`${COOKING_SERVER}/recipe/${id}`)
  .then((resp) => resp.json())
  .then((obj) => {
    console.log("Got active recipe from server", obj);
    try {
      setActive(obj.recipe, obj.dish);
    } catch (e) {
      console.error("Could not set active recipe", e);
      alert(`Could not set active recipe: ${e}`);
      store.set(errorAtom, `Could not set active recipe: ${e}`);
    }
  })
  .catch((err) => {
    console.error("Could not fetch active recipe", err);
    //alert(`Could not fetch active recipe: ${err}`);
    store.set(errorAtom, `Could not fetch active recipe: ${err}`);
  });
}

// iterates by section
function* secIter(bySec: Record<string, any[]>) {
  for (let sec in bySec) {
    const els = bySec[sec];
    for (let i = 0; i < els.length; i++) {
      yield {sec, el: els[i], idx: i};
    }
  }
}

// processes an active-recipe-specific intent
export const processActiveIntent = (intent: any) => {
  const recipe = store.get(currentAtom);
  const {ingredients, steps} = recipe;
  console.log("Got active intent", intent, ingredients, steps);
  // first check for non-ingredient/step intents
  switch (intent.name) {
    case "check_quantity":
      const item = intent.slots.item.value.originalValue.toLowerCase();
      const lst: any = [];
      ingredients.forEach((ingr, idx) => {
        // skip if we've already done this ingredient
        if (ingr.status === "done") {
          return;
        }
        if (ingr.obj.item.toLowerCase().includes(item)) {
          lst.push({section: ingr.section, id: ingr.id, idx, ingr: ingr.obj});
        }
      });
      console.log(`For item ${item} got ${lst.length} results`, lst);
      let s = '';
      switch (lst.length) {
        case 0:
          genSpeech(`Could not find ingredient ${item}`);
          break;
        case 1:
          let {id, ingr} = lst[0];
          if (ingr.min === ingr.max) {
            s = `${ingr.min} ${ingr.unit} of ${ingr.full_item}`;
          } else {
            s = `${ingr.min} to ${ingr.max} ${ingr.unit} of ${ingr.full_item}`;
          }
          genSpeech(s);
          store.set(jumpAtom, id);
          break;
        default:
          let s = `There were ${lst.length} matches for ${item}: `;
          lst.forEach(({id, sec, ingr}) => {
            s += `${ingr.quantity} ${ingr.unit} of ${ingr.item}, in ${sec}. `;
          });
          genSpeech(s);
      }
      return;
    case "focus_recipe":
      setFocused(intent.slots.recipe.value.originalValue);
      return;
  }
  // at this point, we're dealing with an ingredient or step intent
  let [func, obj] = intent.name.split("_");
  if (obj === "ingredient") {
    obj = "ingr";
  }
  const lst: any = obj === "ingr" ? ingredients : steps;
  const idx = lst.findIndex((el: ContainerT<any>) => el.status === "cur");
  let cur = idx >= 0 ? lst[idx] : null;

  // generate speech for the current step/ingredient (after updating statuses)
  const genCurSpeech = (obj: ObjType) => {
    const recipe = store.get(currentAtom);
    const lst = recipe[obj === "ingr" ? "ingredients" : "steps"];
    const idx = lst.findIndex((el: ContainerT<any>) => el.status === "cur");
    const cur: ContainerT<any>|null = idx >= 0 ? lst[idx] : null;
    console.log("in gen cur speech", obj, cur, idx, lst, recipe);
    if (cur) {
      store.set(jumpAtom, cur.id);
      if (obj === "ingr") {
        const curObj: IngredientT = cur.obj;
        genSpeech(`the current ingredient is: ${curObj.min} ${curObj.unit} of ${curObj.full_item}`);
      } else {
        genSpeech(`step ${idx + 1} is: ${cur.obj.text}`);
      }
    }
  };
  switch (func) {
    case "current":
      genCurSpeech(obj);
      break;
    case "finish":
      if (idx >= 0) {
        setStatus({obj, idx, status: "done"}).then(() => {
          genCurSpeech(obj);
        });
      }
      break;
    case "previous":
      for (let i = idx - 1; i >= 0; i--) {
        if (lst[i].status === "todo") {
          setStatus({obj, idx: i, status: "cur"});
          setStatus({obj, idx, status: "todo"}).then(() => {
            genCurSpeech(obj);
          });
          break;
        }
      }
      break;
    case "next":
      for (let i = idx + 1; i < lst.length; i++) {
        if (lst[i].status === "todo") {
          setStatus({obj, idx: i, status: "cur"});
          setStatus({obj, idx, status: "todo"}).then(() => {
            genCurSpeech(obj);
          });
          break;
        }
      }
      break;
    case "remaining":
      const objName = obj === "ingr" ? "ingredient" : obj;
      let s = `There are ${
        lst.filter((el: ContainerT<any>) => el.status !== "done").length
      } remaining ${objName}s`;
      genSpeech(s);
      break;
  }
}

// Converts a float to a fraction
const fractionize = (f: number) => {
  try {
    const ret = new Fraction(f).simplify(0.1).toFraction(true);
    return ret;
  } catch (e) {
    console.log("could not parse quantity", f, typeof e, e);
  }
}

// Sets currently active recipe.
// This takes an "improved" recipe and converts it to the format we use in the app.
// This is a little bit of rearranging, adding instance vars such as status, etc.
export const setActive = (orig: any, dish: Record<string, any> | null) => {
  if (!orig) {
    console.log("setActive got no orig");
    return;
  }
  const  nActive = Object.keys(store.get(activesAtom)).length;
  console.log('setting a new active', orig.name, nActive);
  const r: RecipeT = {
    name: orig.name,
    color: categoricalColor(nActive),
    description: orig.description,
    scale: 1.0,
    images: orig.image || [],
    url: orig.url || orig.mainEntityOfPage || orig['@id'],
    servings: parseFloat(orig.servings || (orig.recipeYield ? orig.recipeYield[0] : '1')),
    ingredients: [],
    steps: [],
    annotations: dish.annotations || [],
    dish,
    times: {},
  };
  // if r.images is a string, enclose it in an array
  if (typeof r.images === 'string') {
    r.images = [r.images];
  }
  // if r.images is a dict, then get the urls out of it
  if (is_dict(r.images) && r.images.url) {
    r.images = [r.images.url];
  }
  console.log("got r from orig", r, orig, dish);
  // parse out ingredients and add within containers
  for (const ingr of secIter(orig.ingredients)) {
    console.debug("got ingr", ingr);
    const el = {...ingr.el};
    el.min = fractionize(el.min);
    el.max = fractionize(el.max);
    el.notes = el.notes || [];
    const cur = {
      obj: el,
      id: el.id,
      section: ingr.sec,
      status: r.ingredients.length === 0 ? "cur" : "todo",
    };
    r.ingredients.push(cur);
  }
  // parse out steps and add within containers
  for (let step of secIter(orig.steps)) {
    console.debug("got step", step);
    const obj = {...step.el};
    obj.ingredients = obj.ingredients || [];
    obj.timers = obj.timers || [];
    const cur = {
      obj,
      id: obj.id,
      section: step.sec,
      status: r.steps.length === 0 ? "cur" : "todo",
    };
    r.steps.push(cur);
  }
  // if we have times in the original, then add them to the recipe
  ['prep', 'cook', 'total'].forEach((k) => {
    const v = orig[k+'Time'];
    if (v){
      // time is in PT format, so parse it
      const m = v.match(/PT(\d+H)?(\d+M)?(\d+S)?/);
      if (m) {
        const H = m[1] ? parseInt(m[1]) : 0;
        const M = m[2] ? parseInt(m[2]) : 0;
        const S = m[3] ? parseInt(m[3]) : 0;
        r.times[k] = H * 60 + M;
      }
    }
  });
  store.set(activesAtom, (actives) => {
    actives[r.name] = r;
  });
  store.set(focusedAtom, r.name);
  console.log("set recipe to ", r.name, r);
}

// sets the recipe with given `name` as the currently focused one
export const setFocused = (name: string) => {
  const actives = store.get(activesAtom);
  if (name in actives) {
    store.set(focusedAtom, name);
  }
}

// sets status of the given thing
export const setStatus = async ({obj, idx, id, status}: {obj: ObjType, idx: number, id: string, status: StatusValue}) => {
  const name = store.get(focusedAtom);
  // if we don't have idx, then lookup by id
  if (idx === undefined) {
    const r = store.get(activesAtom)[name];
    const lst = (obj === "ingr" ? r.ingredients : r.steps);
    idx = lst.findIndex((el: ContainerT<any>) => el.id === id);
    console.log("got idx from id", idx, id, lst);
  }
  console.log("in set status", obj, idx, status);
  // do update inside of an immer block
  store.set(activesAtom, (actives) => {
    const r = actives[name];
    const lst = obj === "step" ? r.steps : r.ingredients;
    console.log('got actives', r, lst);
    // if we mark something as done that was previously the current step,
    // we need to mark the next todo step as the current step
    if (status === "done" && lst[idx].status === "cur") {
      // start at the next step but wrap around to the beginning
      for (let incr = 0; incr < lst.length; incr++) {
        const i = (idx + incr + 1) % lst.length;
        if (lst[i].status === "todo") {
          lst[i].status = "cur";
          break;
        }
      }
    }
    // if we mark something as cur, then unmark any other cur
    if (status === "cur") {
      for (let i = 0; i < lst.length; i++) {
        if (idx !== i && lst[i].status === "cur") {
          lst[i].status = "todo";
        }
      }
    }
    lst[idx].status = status;
  });
}

// gets ingredient quantity, based on recipe scaling
export const getIngrQuantity = (ingr: IngredientT, recipe: RecipeT) => {
  //console.log('getting quantity', ingr.min, typeof(ingr.min), ingr.max, recipe.scale);
  // scale a value which might be a number, string, or fraction
  const scaled = (f) => {
    let v = 0;
    if (typeof f === 'number') {
      v = f;
    } else if (typeof f === 'string') {
      v = Fraction(f).valueOf();
    } else if (f instanceof Fraction) {
      v = f.valueOf();
    } else {
      throw new Error(`could not convert ${f} (${typeof f}) to decimal`);
    }
    v *= recipe.scale;
    // convert back to fractional string
    return fractionize(v);
  }
  let min = scaled(ingr.min);
  let max = scaled(ingr.max);
  const quant = (min === max) ? min : `${min}-${max}`;
  return quant;
}

